import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { AuthShell, Field } from "@/pages/Login";
import GoogleButton from "@/components/GoogleButton";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite") || "";
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/invites/${inviteToken}`).then((r) => {
      if (r.data.valid) { setInvite(r.data); if (r.data.email) setEmail(r.data.email); }
    }).catch(() => {});
  }, [inviteToken]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register(email, password, name, inviteToken || undefined);
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const roleLabel = { beta_tester: "Beta Tester", admin: "Admin", customer: "Client" }[invite?.role] || invite?.role;

  return (
    <AuthShell title="Create your studio" subtitle="Start building sites in seconds.">
      {invite && (
        <div data-testid="invite-banner" className="mb-5 flex items-center gap-2 bg-[#0055ff]/10 border border-[#0055ff] px-3 py-2.5 text-sm">
          <Sparkles size={15} className="text-[#0055ff] shrink-0" />
          <span>You've been invited to join as <b>{roleLabel}</b>. Sign up below to activate it.</span>
        </div>
      )}
      <GoogleButton label="Sign up with Google" />
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-[#d3d3d3]" />
        <span className="label-tech">or</span>
        <div className="flex-1 h-px bg-[#d3d3d3]" />
      </div>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full name" testid="register-name" value={name} onChange={setName} />
        <Field label="Email" testid="register-email" type="email" value={email} onChange={setEmail} />
        <Field label="Password" testid="register-password" type="password" value={password} onChange={setPassword} />
        {error && <div data-testid="register-error" className="text-sm text-[#ff3b00] font-tech">{error}</div>}
        <button data-testid="register-submit" disabled={busy} className="w-full bg-[#ff3b00] text-white font-semibold py-3 flex items-center justify-center gap-2 hover:bg-[#e63500] disabled:opacity-60 transition-colors">
          {busy ? "Creating…" : (<>Create account <ArrowRight size={16} /></>)}
        </button>
      </form>
      <p className="mt-6 label-tech opacity-70">Already have an account? <Link to="/login" className="text-[#ff3b00]">Log in</Link></p>
    </AuthShell>
  );
}
